import React, { useEffect, useState } from 'react';
import { useLoading } from '../../contexts/LoadingContext';
import { getAllOrders } from '../../services/order.service';
import api from '../../services/api';
import ActiveUsersWidget from '../../components/admin/ActiveUsersWidget';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { ShoppingCart, Package, PackageCheck, XCircle } from 'lucide-react';

// Validated categorical palette (dataviz skill) — fixed hue order, not cycled
const COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#7a5cd6', '#d64f8f'];
const ACTIVE_METRIC_COLOR = { revenue: '#2a78d6', profit: '#eb6834' };

const ORDER_STATUS_LABELS = {
  pending: 'Pending',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled'
};

const PAYMENT_METHOD_LABELS = {
  'pay-now': 'Pay Now (Online)',
  'pay-later': 'Pay Later (Cash on Delivery)'
};

const emptyDashboardStats = {
  totalRevenue: 0,
  netProfitEstimate: 0,
  estimatedCOGS: 0,
  vendorSourcedRevenue: 0,
  monthlyTrend: [],
  revenueDistribution: [],
  orderAnalytics: {
    periodDays: 30,
    totalOrders: 0,
    revenue: 0,
    averageOrderValue: 0,
    cancellationRate: 0,
    deliveredAmount: 0,
    cancelledAmount: 0,
    statusBreakdown: [],
    paymentMethodBreakdown: []
  }
};

const AdminDashboard = () => {
  const { startLoading, stopLoading } = useLoading();
  const [activeMetric, setActiveMetric] = useState('revenue');
  const [periodDays, setPeriodDays] = useState(30);
  const [stats, setStats] = useState({
    products: 0,
    ordersThisMonth: 0
  });
  const [dashboardStats, setDashboardStats] = useState(emptyDashboardStats);

  useEffect(() => {
    const loadDashboardData = async () => {
      startLoading();
      try {
        // Fetch products count
        const productsRes = await api.get('/products/admin/list', {
          params: { page: 1, limit: 1 }
        });
        const productsCount = productsRes.data?.data?.pagination?.totalItems || 0;

        // Fetch orders count for this month
        const currentDate = new Date();
        const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);

        const ordersRes = await getAllOrders({
          page: 1,
          limit: 1000,
          startDate: firstDayOfMonth.toISOString(),
          endDate: lastDayOfMonth.toISOString()
        });
        const ordersCount = ordersRes.data?.pagination?.total || 0;

        // Fetch real financial/order analytics
        const dashboardRes = await api.get('/orders/admin/dashboard-stats', {
          params: { days: periodDays }
        });

        setStats({
          products: productsCount,
          ordersThisMonth: ordersCount
        });
        setDashboardStats(dashboardRes.data?.data || emptyDashboardStats);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        stopLoading();
      }
    };
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodDays]);

  const formatCurrency = (value) => `Rs.${Math.round(value || 0).toLocaleString()}`;

  const getPerformanceStatus = (value) => {
    if (value >= 10) return { label: 'Excellent', color: 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30' };
    if (value >= 5) return { label: 'Good', color: 'text-blue-700 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30' };
    if (value >= 0) return { label: 'Stable', color: 'text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30' };
    return { label: 'Needs Attention', color: 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30' };
  };

  const { monthlyTrend, revenueDistribution, orderAnalytics } = dashboardStats;

  // Real, computed-from-data insights (nothing fabricated)
  const highestMonth = monthlyTrend.reduce(
    (best, m) => (m.revenue > (best?.revenue ?? -1) ? m : best),
    null
  );
  const firstMonthRevenue = monthlyTrend[0]?.revenue || 0;
  const lastMonthRevenue = monthlyTrend[monthlyTrend.length - 1]?.revenue || 0;
  const sixMonthGrowthPercent = firstMonthRevenue > 0
    ? Math.round(((lastMonthRevenue - firstMonthRevenue) / firstMonthRevenue) * 1000) / 10
    : null;
  const sixMonthTotalRevenue = monthlyTrend.reduce((sum, m) => sum + m.revenue, 0);

  const profitMarginPercent = dashboardStats.vendorSourcedRevenue > 0
    ? (dashboardStats.netProfitEstimate / dashboardStats.vendorSourcedRevenue) * 100
    : 0;

  const topCategory = revenueDistribution[0];
  const totalCategoryRevenue = revenueDistribution.reduce((sum, c) => sum + c.revenue, 0);

  const plStatement = [
    { name: 'Revenue', value: dashboardStats.totalRevenue },
    { name: 'Estimated Cost of Goods Sold', value: -dashboardStats.estimatedCOGS },
    { name: 'Net Profit (Estimated)', value: dashboardStats.netProfitEstimate }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-admin-prussian-500 via-admin-indigo-500 to-admin-ucla-500 p-8 shadow-md">
        <div className="relative z-10">
          <h1 className="text-2xl font-bold text-white mb-2">Financial Dashboard</h1>
          <p className="text-admin-sky-800">
            Comprehensive overview of your business performance
          </p>
        </div>
        <div className="absolute right-0 top-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="absolute right-16 bottom-0 w-40 h-40 bg-gradient-to-tr from-admin-cerulean-500/30 to-transparent rounded-full transform translate-y-16"></div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Order Value"
          value={formatCurrency(dashboardStats.totalRevenue)}
          icon={RsIcon}
          color={COLORS[0]}
        />
        <MetricCard
          title="Total Delivered Amount"
          value={formatCurrency(orderAnalytics.deliveredAmount)}
          icon={PackageCheck}
          color={COLORS[2]}
        />
        <MetricCard
          title="Cancelled Amount"
          value={formatCurrency(orderAnalytics.cancelledAmount)}
          icon={XCircle}
          color="#d64f4f"
        />
        <MetricCard
          title="Orders (This Month)"
          value={stats.ordersThisMonth.toString()}
          icon={ShoppingCart}
          color={COLORS[1]}
        />
        <MetricCard
          title="Total Products"
          value={stats.products.toString()}
          icon={Package}
          color="#4a3aa7"
        />
        <ActiveUsersWidget />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue & Profit Trends */}
        <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-admin-slate-900 dark:text-admin-slate-100">Revenue & Profit Trends</h2>
            <div className="flex items-center space-x-2">
              <MetricButton
                active={activeMetric === 'revenue'}
                onClick={() => setActiveMetric('revenue')}
                label="Revenue"
                color={ACTIVE_METRIC_COLOR.revenue}
              />
              <MetricButton
                active={activeMetric === 'profit'}
                onClick={() => setActiveMetric('profit')}
                label="Profit"
                color={ACTIVE_METRIC_COLOR.profit}
              />
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyTrend}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={ACTIVE_METRIC_COLOR.revenue} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={ACTIVE_METRIC_COLOR.revenue} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={ACTIVE_METRIC_COLOR.profit} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={ACTIVE_METRIC_COLOR.profit} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Area
                  type="monotone"
                  dataKey={activeMetric}
                  stroke={ACTIVE_METRIC_COLOR[activeMetric]}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill={`url(#${activeMetric === 'revenue' ? 'colorRevenue' : 'colorProfit'})`}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {/* Real, computed summary */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-admin-slate-200 dark:border-admin-slate-700">
            <div>
              <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">6-Month Growth</p>
              <p className="mt-2 flex items-baseline gap-x-2">
                <span className="text-lg font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                  {sixMonthGrowthPercent === null ? 'N/A' : `${sixMonthGrowthPercent}%`}
                </span>
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Highest Month</p>
              <p className="mt-2 flex items-baseline gap-x-2">
                <span className="text-lg font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                  {highestMonth?.revenue > 0 ? highestMonth.name : 'N/A'}
                </span>
                {highestMonth?.revenue > 0 && (
                  <span className="text-sm text-admin-slate-500">{formatCurrency(highestMonth.revenue)}</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Total (6 Months)</p>
              <p className="mt-2 flex items-baseline gap-x-2">
                <span className="text-lg font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                  {formatCurrency(sixMonthTotalRevenue)}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* P&L Statement */}
        <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-admin-slate-900 dark:text-admin-slate-100">P&L Statement</h2>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              getPerformanceStatus(profitMarginPercent).color
            }`}>
              {getPerformanceStatus(profitMarginPercent).label}
            </span>
          </div>
          <div className="w-full">
            <table className="w-full divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
              <thead className="bg-admin-slate-50 dark:bg-admin-slate-800/50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">
                    Item
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-admin-slate-800 divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
                {plStatement.map((item) => (
                  <tr
                    key={item.name}
                    className={item.name === 'Net Profit (Estimated)' ? 'bg-admin-slate-50 dark:bg-admin-slate-700/50 font-medium' : ''}
                  >
                    <td className="px-4 py-4 text-sm text-admin-slate-900 dark:text-admin-slate-100">
                      {item.name}
                    </td>
                    <td className={`px-4 py-4 text-sm text-right ${
                      item.value < 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-admin-slate-900 dark:text-admin-slate-100'
                    }`}>
                      {formatCurrency(Math.abs(item.value))}
                      {item.value < 0 ? ' (-)' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-admin-slate-500 dark:text-admin-slate-400">
            Profit is estimated using each product's vendor cost price where available.
            Products without a tracked vendor cost are excluded from the cost/profit calculation.
          </p>
        </div>

        {/* Order Analytics */}
        <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-admin-slate-900 dark:text-admin-slate-100">Order Analytics</h2>
            <div className="flex items-center space-x-2">
              <select
                value={periodDays}
                onChange={(e) => setPeriodDays(Number(e.target.value))}
                className="text-sm border-0 bg-admin-slate-100 dark:bg-admin-slate-700 rounded-md py-1 px-3 text-admin-slate-600 dark:text-admin-slate-300"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
          </div>

          {/* Chart */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="orders" name="Orders" stroke={COLORS[0]} strokeWidth={2} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Stats Section */}
          <div className="mt-6 pt-6 border-t border-admin-slate-200 dark:border-admin-slate-700">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
              <div>
                <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Total Orders</p>
                <div className="mt-2 flex items-baseline gap-2">
                  <p className="text-2xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">{orderAnalytics.totalOrders}</p>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Revenue</p>
                <div className="mt-2 flex items-baseline gap-2">
                  <p className="text-2xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">{formatCurrency(orderAnalytics.revenue)}</p>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Average Value</p>
                <div className="mt-2 flex items-baseline gap-2">
                  <p className="text-2xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">{formatCurrency(orderAnalytics.averageOrderValue)}</p>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Cancellation Rate</p>
                <div className="mt-2 flex items-baseline gap-2">
                  <p className="text-2xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">{orderAnalytics.cancellationRate}%</p>
                </div>
              </div>
            </div>

            {/* Order Status & Payment Methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400 mb-4">Order Status</h4>
                {orderAnalytics.statusBreakdown.length === 0 ? (
                  <p className="text-sm text-admin-slate-500 dark:text-admin-slate-400">No orders in this period</p>
                ) : (
                  <table className="w-full">
                    <tbody className="divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
                      {orderAnalytics.statusBreakdown.map((s) => (
                        <tr key={s.status}>
                          <td className="py-2 text-sm text-admin-slate-600 dark:text-admin-slate-400">
                            {ORDER_STATUS_LABELS[s.status] || s.status}
                          </td>
                          <td className="py-2 text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100 text-right">{s.count}</td>
                          <td className="py-2 text-sm text-admin-slate-500 dark:text-admin-slate-400 text-right w-20">{s.percent}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div>
                <h4 className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400 mb-4">Payment Methods</h4>
                {orderAnalytics.paymentMethodBreakdown.length === 0 ? (
                  <p className="text-sm text-admin-slate-500 dark:text-admin-slate-400">No orders in this period</p>
                ) : (
                  <table className="w-full">
                    <tbody className="divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
                      {orderAnalytics.paymentMethodBreakdown.map((p) => (
                        <tr key={p.method}>
                          <td className="py-2 text-sm text-admin-slate-600 dark:text-admin-slate-400">
                            {PAYMENT_METHOD_LABELS[p.method] || p.method}
                          </td>
                          <td className="py-2 text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100 text-right">{p.count}</td>
                          <td className="py-2 text-sm text-admin-slate-500 dark:text-admin-slate-400 text-right w-20">{p.percent}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Distribution */}
        <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-medium text-admin-slate-900 dark:text-admin-slate-100 mb-6">Revenue Distribution</h2>
          <div className="h-80">
            {revenueDistribution.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-admin-slate-500 dark:text-admin-slate-400">
                No sales data yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={revenueDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="revenue"
                    nameKey="category"
                    label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                  >
                    {revenueDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          {/* Real category summary */}
          {topCategory && (
            <div className="mt-6 pt-6 border-t border-admin-slate-200 dark:border-admin-slate-700">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Top Category</p>
                  <p className="mt-1 font-semibold text-admin-slate-900 dark:text-admin-slate-100">{topCategory.category}</p>
                  <p className="text-xs text-green-500">
                    {totalCategoryRevenue > 0 ? Math.round((topCategory.revenue / totalCategoryRevenue) * 1000) / 10 : 0}% of tracked revenue
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Categories Selling</p>
                  <p className="mt-1 font-semibold text-admin-slate-900 dark:text-admin-slate-100">{revenueDistribution.length}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RsIcon = ({ className }) => (
  <span className={`${className} flex items-center justify-center font-bold text-[11px]`}>
    Rs
  </span>
);

const MetricCard = ({ title, value, details, icon: Icon, color = '#2a78d6' }) => (
  <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm border border-admin-slate-100 dark:border-admin-slate-700 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">{title}</p>
      {Icon && (
        <span
          className="flex h-9 w-9 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${color}1a`, color }}
        >
          <Icon className="h-5 w-5" strokeWidth={2} />
        </span>
      )}
    </div>
    <div className="mt-2 flex items-baseline">
      <p className="text-2xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">{value}</p>
    </div>
    {details && <p className="mt-1 text-xs text-admin-slate-500 dark:text-admin-slate-400">{details}</p>}
  </div>
);

const MetricButton = ({ active, onClick, label, color = '#2a78d6' }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
      active
        ? 'text-white shadow-sm'
        : 'text-admin-slate-600 dark:text-admin-slate-400 hover:bg-admin-slate-100 dark:hover:bg-admin-slate-700'
    }`}
    style={active ? { backgroundColor: color } : undefined}
  >
    {label}
  </button>
);

export default AdminDashboard;
